import React, { useState, useEffect } from "react";
import { Box, IconButton, Typography, Avatar, Badge, Tooltip } from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon, NotificationsActive } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "../services/customerApi";
import logo from "../assets/logoo.png";

const getAvatarSrc = (picStr) => {
  if (!picStr) return "";
  if (picStr.startsWith("http")) return picStr;
  if (picStr.startsWith("avatar_")) {
    try {
      return require(`../assets/avatars/${picStr}`);
    } catch (e) {
      return "";
    }
  }
  return `${BASE_URL}${picStr}`;
};

const ClickableBox = styled(Box)(() => ({
  cursor: "pointer",
  "&:hover": { opacity: 0.8 }
}));

export default function AppBarTop(props) {
  const { drawerOpen, toggleDrawer, setActiveSection, setStatusFilter, userName, expiringSoonCount } = props;
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard" || location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const profilePic = sessionStorage.getItem("profilePic");
  const displayPic = getAvatarSrc(profilePic);



  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      setScrolled(currentScrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Animated gradient based on scroll
  const getBackground = () => {
    if (!scrolled) return "transparent";

    return `
      linear-gradient(
        90deg,
        rgba(10, 10, 10, 0.98) 0%,
        rgba(20, 20, 20, 0.98) 100%
      )
    `;
  };

  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 20,
        delay: 0.1
      }}
    >
      <Box
        sx={{
          position: "fixed",
          height: { xs: 70, md: 90 },
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: { xs: "10px 15px", md: "15px 25px" },
          zIndex: 1000,
          background: getBackground(),
          backdropFilter: scrolled ? "blur(20px)" : "blur(0px)",
          WebkitBackdropFilter: scrolled ? "blur(20px)" : "blur(0px)",
          borderBottom: scrolled
            ? `1px solid rgba(253, 184, 19, 0.2)`
            : "none",
          boxShadow: scrolled
            ? `0 5px 30px rgba(0, 0, 0, 0.6)`
            : "none",
          transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: scrolled
              ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.03), transparent)"
              : "none",
            transform: "translateX(-100%)",
            animation: scrolled ? "shine 8s infinite linear" : "none",
            pointerEvents: "none"
          }
        }}
      >
        {/* Floating particles effect */}
        {scrolled && (
          <>
            <Box sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
              "& span": {
                position: "absolute",
                width: "3px",
                height: "3px",
                background: "rgba(255, 255, 255, 0.3)",
                borderRadius: "50%",
                animation: "floatParticle 6s infinite linear",
                "&:nth-of-type(1)": { top: "20%", left: "10%", animationDelay: "0s" },
                "&:nth-of-type(2)": { top: "40%", left: "85%", animationDelay: "1s" },
                "&:nth-of-type(3)": { top: "60%", left: "30%", animationDelay: "1s" },
                "&:nth-of-type(4)": { top: "80%", left: "70%", animationDelay: "1s" },
                "&:nth-of-type(5)": { top: "10%", left: "50%", animationDelay: "1s" },
                "&:nth-of-type(6)": { top: "30%", left: "20%", animationDelay: "1s" },
                "&:nth-of-type(7)": { top: "50%", left: "90%", animationDelay: "1s" },
                "&:nth-of-type(8)": { top: "70%", left: "40%", animationDelay: "1s" },
                "&:nth-of-type(9)": { top: "90%", left: "60%", animationDelay: "1s" },
                "&:nth-of-type(10)": { top: "5%", left: "15%", animationDelay: "1s" },
                "&:nth-of-type(11)": { top: "25%", left: "75%", animationDelay: "1s" },
                "&:nth-of-type(12)": { top: "45%", left: "35%", animationDelay: "1s" },
                "&:nth-of-type(13)": { top: "65%", left: "55%", animationDelay: "1s" },
                "&:nth-of-type(14)": { top: "85%", left: "15%", animationDelay: "1s" },
                "&:nth-of-type(15)": { top: "15%", left: "95%", animationDelay: "4s" }
              }
            }}>
              {[...Array(15)].map((_, i) => (
                <span key={i}></span>
              ))}
            </Box>
          </>
        )}

        {/* LEFT — MENU + BRAND */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 2 }, position: "relative", zIndex: 1 }}>
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <IconButton
              onClick={toggleDrawer}
              onMouseEnter={() => setIsMenuHovered(true)}
              onMouseLeave={() => setIsMenuHovered(false)}
              sx={{
                color: (isDashboard && !scrolled) ? "#000000ff" : "#ffffff",
                width: { xs: 35, md: 40 },
                height: { xs: 35, md: 40 },
                minWidth: { xs: 35, md: 40 },
                minHeight: { xs: 35, md: 40 },
                background: scrolled
                  ? "rgba(253, 184, 19, 0.1)"
                  : "rgba(255, 255, 255, 0.05)",
                transition: "all 0.3s ease",
                border: scrolled
                  ? "1px solid rgba(253, 184, 19, 0.3)"
                  : "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  background: "rgba(253, 184, 19, 0.2)",
                  transform: "scale(1.1)",
                  boxShadow: "0 0 20px rgba(253, 184, 19, 0.4)"
                }
              }}
            >
              <motion.div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                animate={{ rotate: isMenuHovered ? (drawerOpen ? -180 : 180) : 0 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {drawerOpen ? <CloseIcon sx={{ fontSize: { xs: 18, md: 24 } }} /> : <MenuIcon sx={{ fontSize: { xs: 18, md: 24 } }} />}
              </motion.div>
            </IconButton>
          </motion.div>

          <ClickableBox onClick={() => setActiveSection(sessionStorage.getItem("userRole") === "MARKETER" ? "users" : "dashboard")}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Typography
                variant="h5"
                fontWeight={900}
                sx={{
                  color: "white",
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "1px",
                  fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.6rem" },
                  transition: "all 0.4s ease",
                  position: "relative",
                  display: { xs: "none", sm: "block" },
                  background: (isDashboard && !scrolled) ? "linear-gradient(90deg, #000000ff, #ffef0eff, #000000ff)" : "linear-gradient(90deg, #ffffff, #FDB813, #ffffff)",
                  backgroundSize: "200% auto",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "textShine 3s infinite linear",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -4,
                    left: 0,
                    width: "100%",
                    height: "2px",
                    background: "linear-gradient(90deg, transparent, #FDB813, transparent)",
                    transform: "scaleX(0)",
                    transition: "transform 0.4s ease"
                  },
                  "&:hover::after": {
                    transform: "scaleX(1)"
                  }
                }}
              >
                GateWay Insurance
              </Typography>
            </motion.div>
          </ClickableBox>
        </Box>

        {/* RIGHT — ADMIN NAME + LOGO */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, md: 2 },
          position: "relative",
          zIndex: 1
        }}>
          {/* Expiring Soon Alert
          {expiringSoonCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <Tooltip title={`${expiringSoonCount} policies expiring soon`} arrow>
                <IconButton
                  onClick={() => {
                    setStatusFilter("expiring soon");
                    setActiveSection("LeadModule");
                  }}
                  sx={{
                    color: "#ffffff",
                    background: "rgba(244, 67, 54, 0.1)",
                    border: "1px solid rgba(244, 67, 54, 0.2)",
                    padding: { xs: "6px", md: "8px" },
                    position: "relative",
                    overflow: "visible",
                    "&:hover": {
                      background: "rgba(244, 67, 54, 0.2)",
                      boxShadow: "0 0 15px rgba(244, 67, 54, 0.4)",
                      transform: "scale(1.05)"
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "50%",
                      border: "2px solid #f44336",
                      animation: "pulseRed 1.5s infinite ease-in-out",
                      boxShadow: "0 0 10px #f44336",
                      opacity: 0.6,
                      pointerEvents: "none"
                    }}
                  />
                  <Badge
                    badgeContent={expiringSoonCount}
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.65rem",
                        height: "18px",
                        minWidth: "18px",
                        fontWeight: 900,
                        backgroundColor: "#f44336",
                        color: "#ffffff",
                        // border: "1.5px solid #0a0a0a",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                        animation: "pulseShadow 2s infinite",
                        top: 0,
                        right: 0,
                      }
                    }}
                  >
                    <NotificationsActive
                      sx={{
                        fontSize: { xs: 20, md: 24 },
                        color: "#f44336",
                        filter: "drop-shadow(0 0 3px rgba(244, 67, 54, 0.4))",
                        animation: "bellRing 3s infinite ease-in-out"
                      }}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            </motion.div>
          )} */}

          {/* Admin Profile with Enhanced Effects */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: 2 },
                backgroundColor: scrolled
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(253, 184, 19, 0.05)",
                padding: { xs: "4px 8px", md: "5px 16px 5px 6px" },
                borderRadius: "50px",
                border: scrolled
                  ? "1px solid rgba(255, 255, 255, 0.15)"
                  : "1px solid rgba(253, 184, 19, 0.2)",
                position: "relative",
                cursor: "pointer",
                transition: "all 0.4s ease",
                "&:hover": {
                  backgroundColor: "rgba(253, 184, 19, 0.12)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.4)"
                }
              }}
            >
              {/* Avatar with rotating rings */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", width: { xs: 30, md: 40 }, height: { xs: 30, md: 40 } }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                  style={{
                    position: "absolute",
                    width: "125%",
                    height: "125%",
                    borderRadius: "50%",
                    border: "2px solid transparent",
                    borderTopColor: "#FDB813",
                    borderBottomColor: "#FDB813",
                    opacity: 0.6,
                    zIndex: 0
                  }}
                />

                <Avatar
                  src={displayPic}
                  sx={{
                    width: "100%",
                    height: "100%",
                    background: "transparent",
                    color: "#000000",
                    fontSize: { xs: "0.75rem", md: "0.95rem" },
                    fontWeight: 900,
                    position: "relative",
                    zIndex: 2,
                    border: "2px solid #FDB813",
                    boxShadow: "0 0 15px rgba(253, 184, 19, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => setActiveSection(sessionStorage.getItem("userRole") === "MARKETER" ? "users" : "profile")}
                >
                  {!displayPic && (
                    <Box component="span" sx={{ lineHeight: 1, mt: "-1px" }}>
                      {userName.slice(0, 2).toUpperCase()}
                    </Box>
                  )}
                </Avatar>

                {/* Status indicator with pulse */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [1, 0.8, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    position: "absolute",
                    bottom: -1,
                    right: -1,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#4caf50",
                    zIndex: 5,
                    border: "2px solid #111111",
                    boxShadow: "0 0 5px rgba(76, 175, 80, 0.5)"
                  }}
                />
              </Box>

              {/* Admin Name */}
              <Box sx={{ display: { xs: "none", md: "block" }, ml: 1 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{
                    fontFamily: "'Outfit', sans-serif",
                    color: (isDashboard && !scrolled) ? "#0f420cff" : "#ffffff",
                    letterSpacing: "0.5px",
                    fontSize: "0.9rem",
                    lineHeight: 1,
                    mt: "1px"
                  }}
                >
                  {userName.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </motion.div>

          {/* Rounded Company Logo with Glow */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                width: { xs: 35, md: 45 },
                height: { xs: 35, md: 45 },
                borderRadius: "50%", // Full circle as requested
                objectFit: "cover",
                cursor: "pointer",
                border: "2px solid #FDB813",
                boxShadow: "0 0 15px rgba(253, 184, 19, 0.4)",
                transition: "all 0.3s ease",
                background: "#0a0a0a"
              }}
              onClick={() => setActiveSection(sessionStorage.getItem("userRole") === "MARKETER" ? "users" : "dashboard")}
            />
          </motion.div>
        </Box>


        {/* Add global styles for animations */}
        <style>{`
          @keyframes textShine {
            0% { background-position: 0% center; }
            100% { background-position: 300% center; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.2); }
          }
          @keyframes shine {
            0% { transform: translateX(-100%) rotate(0deg); }
            100% { transform: translateX(100%) rotate(0deg); }
          }
          @keyframes floatParticle {
            0% { transform: translateY(0) translateX(0); opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translateY(-100px) translateX(100px); opacity: 0; }
          }
          @keyframes pulseRed {
            0% { transform: scale(1); opacity: 0.8; }
            50% { transform: scale(1.5); opacity: 0; }
            100% { transform: scale(1); opacity: 0; }
          }
          @keyframes pulseShadow {
            0%, 100% { box-shadow: 0 0 5px rgba(244, 67, 54, 0.8); }
            50% { box-shadow: 0 0 15px rgba(244, 67, 54, 1); }
          }
          @keyframes bellRing {
            0%, 50%, 100% { transform: rotate(0deg); }
            5%, 15%, 25%, 35% { transform: rotate(15deg); }
            10%, 20%, 30%, 40% { transform: rotate(-15deg); }
          }
          @keyframes rotateGlow {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </Box>
    </motion.div>
  );
}